import React, {
  Fragment,
  ReactChild,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import qs from 'query-string';
import cc from 'classcat';
import type {
  CommunityDetails,
  CommunityMember,
} from '@cocoplatform/coco-rtc-shared';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import C from './CommunityMembersExplorer.css';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CommentIcon from '@mui/icons-material/Comment';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import CheckIcon from '@mui/icons-material/Check';
import { bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Divider from '@mui/material/Divider';
import RoleList from 'components/role-list/RoleList';
import { reportServerError } from 'utils/report-error';
import compact from 'lodash/compact';
import { useCommunityDetails } from 'atoms/community-details';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import CloseIcon from '@mui/icons-material/Close';
import { InlineList } from 'components/inline-list/InlineList';
import { DashboardParams } from 'constants/routes';
import TagList from 'components/tag-list/TagList';
import EmptyMessage from 'components/empty-message/EmptyMessage';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import NewGroupControl from 'components/new-group-modal/NewGroupControl';
import FlexSep from 'components/flex/FlexSep';
import FlexRow from 'components/flex/FlexRow';
import { confirmRemember } from 'utils/confirm';
import GroupMenuTrigger from 'components/new-group-modal/GroupMenuTrigger';
import { shouldUseNewWindow, visitUrl } from 'utils/url';
import UserAvatar from 'components/user-avatar/UserAvatar';
import {
  getSameOrLowerRoles,
  MembershipRole,
  COMMUNITY_MEMBERSHIP_ROLES,
} from 'constants/community-roles';
import { qualifiedUserName } from 'utils/user';
import uniq from 'lodash/uniq';
import Tooltip from '@mui/material/Tooltip';
import { useSearchParam } from 'utils/url-util';
import ShareIcon from '!!react-svg-loader!../space-card/icons/icon--space-share.svg';
import EditIcon from '!!react-svg-loader!../space-details-modal/icons/icon--space-edit.svg';
import { useUserProfile } from 'atoms/user-profile';
import { useStore } from '@nanostores/react';
import UsernameEditModal from 'components/username-edit-modal/UsernameEditModal';
import { CommunityUser } from '@cocoplatform/coco-rtc-shared';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import {
  Badge,
  Box,
  ButtonGroup,
  ClickAwayListener,
  Fade,
  Grid,
  IconButton,
  Input,
  ListItemButton,
  OutlinedInput,
  Paper,
  Popover,
  Popper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CommunityJoinLink from 'components/community-join-link/CommunityJoinLink';
import { GroupChip } from 'components/space-edit-modal/InternalSpaceEditModal';
import { notificationsEvt } from 'components/notification-menu/NotificationTrigger';
import { useEvt } from 'evt/hooks';
import { NOTIFICATION_EVENTS } from '@cocoplatform/coco-rtc-shared';
import { Trans, msg, t } from '@lingui/macro';
import _ from 'lodash';
import { useLingui } from '@lingui/react';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import DotSeparator from 'components/item-list/Separator';
import BlockIcon from '@mui/icons-material/Block';
import ReportIcon from '@mui/icons-material/Report';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useModal } from 'mui-modal-provider';
import AddMembersEmailModal from 'components/add-members-modal/AddMembersEmailModal';
import AddMembersLoginModal, {
  DownloadCSVButton,
} from 'components/add-members-modal/AddMembersLoginModal';
import {
  Cancel,
  CancelRounded,
  Delete,
  ModeEdit,
  Settings,
  StopCircle,
} from '@mui/icons-material';
import { EMAIL_REGEX } from 'utils/validation';
import Swal from 'sweetalert2';
import { LoadingButton } from '@mui/lab';
import swal from 'sweetalert2';
import { useTutorial } from 'components/onboarding-tutorials/tutorial-atom';
import ImportGoogleClassroomModal from 'components/google-classroom-modal/GoogleClasroomModal';
import GoogleIcon from '@mui/icons-material/Google';
import { useFlags } from 'utils/feature-flags';
import GoogleClassroomIcon from './google-classroom.png';
import { EVENTS, INTERACTIONS, useTrack } from 'utils/mixpanel';
import { userProfileAtom } from 'atoms/user-profile-atom';

interface FilterTag {
  id: string;
  label: string;
  managerOnly?: boolean;
  icon?: ReactChild;
  more?: boolean;
}

const getMemberFilterTags = (): FilterTag[] => [
  {
    id: '$all',
    label: t`All`,
    managerOnly: false,
  },
  {
    id: 'adminsAndManagers',
    label: t`Admins / Managers`,
    managerOnly: false,
    icon: <AdminPanelSettingsIcon />,
  },
  {
    id: 'emailMembers',
    label: t`Members`,
    managerOnly: false,
    icon: <EmailIcon />,
  },
  {
    id: 'loginMembers',
    label: t`Members`,
    managerOnly: false,
    icon: <KeyIcon />,
  },
  {
    id: 'blocked',
    label: t`Blocked`,
    managerOnly: true,
    more: true,
    icon: <BlockIcon />,
  },
  {
    id: 'reported',
    label: t`Reported`,
    managerOnly: true,
    more: true,
    icon: <ReportIcon />,
  },
  {
    id: 'pendingApproval',
    label: t`Join Requests`,
    managerOnly: true,
    more: true,
    icon: <PendingActionsIcon />,
  },
  {
    id: 'invited',
    label: t`Invited`,
    managerOnly: true,
    more: true,
    icon: <PersonAddAltIcon />,
  },
];

type MemberGroupId =
  | 'pendingApproval'
  | 'admin'
  | 'managers'
  | 'adminOrManagers'
  | 'participants'
  | 'blocked'
  | 'reported'
  | 'pendingInvitedAdminOrManagers'
  | 'pendingInvitedHosts'
  | 'pendingInvitedParticipants'
  | 'emailMembers'
  | 'loginMembers';

type MemberGroups = Record<MemberGroupId, CommunityMember[]>;

export function AddMembersButton({
  communityId,
  variant,
  text,
  className,
  icon,
  onClick,
  onModalOpen,
  onCloseModal,
  onSubmit,
  theme,
  customBtn,
}: {
  communityId: string;
  variant?: 'contained' | 'text';
  text?: ReactNode;
  className?: string;
  icon?: ReactChild;
  onClick?: () => void;
  onCloseModal?: () => void;
  onModalOpen?: () => void;
  onSubmit?: () => void;
  theme?: 'contained' | 'filled';
  customBtn?: ReactNode;
}) {
  const { _ } = useLingui();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const arrowRef = React.useRef(null);

  const { showModal } = useModal();

  const { stopTutorial } = useTutorial();

  const { community } = useCommunityDetails({
    communityId,
    autoLoad: false,
  });

  const { google_classroom_integration } = useFlags([
    'google_classroom_integration',
  ]);

  const track = useTrack();

  const resolvedTheme: 'contained' | 'filled' = theme || 'filled';

  const defaultIcon =
    resolvedTheme === 'contained' ? (
      <PersonAddAltIcon
        style={{
          marginRight: '0.3rem',
          width: '1.2rem',
          height: '1.2rem',
          fontWeight: 700,
          color: 'var(--color-primary)',
          marginBottom: '2px',
        }}
      />
    ) : (
      <PersonAddAltIcon
        style={{
          color: variant === 'contained' ? 'white' : undefined,
          marginRight: '0.5rem',
          width: '1.3rem',
          height: '1.3rem',
          fontWeight: 700,
          marginBottom: '2px',
        }}
      />
    );

  const defaultText =
    resolvedTheme === 'contained' ? (
      <Typography sx={{ color: 'var(--color-primary)' }} component='span' fontWeight='bold'>
        <Trans>Add</Trans>
      </Typography>
    ) : (
      <Typography component='span' fontWeight='bold' sx={{ color: 'white' }}>
        <Trans>Add Members</Trans>
      </Typography>
    );

  const handleClick = async (e) => {
    track(EVENTS.ADD_MEMBERS, INTERACTIONS.CLICK);
    stopTutorial();
    if (onClick) {
      onClick();
    }

    if (community?.corridorType === 'PERSONAL') {
      const modal = showModal(AddMembersEmailModal, {
        onClose: () => {
          modal.destroy();
          onCloseModal?.();
        },
        onSubmit,
        communityId,
      });

      onModalOpen?.();
      return;
    }

    setAnchorEl(e.currentTarget);
  }

  return (
    <>
      {customBtn ? <div onClick={handleClick}>{customBtn}</div> : <Button
        variant={variant || 'contained'}
        className={className || cc([C.primaryBtn])}
        style={{
          padding: '0.3rem',
          borderColor: 'var(--color-primary) !important',
        }}
        onClick={handleClick}
        size='small'
      >
        {icon ?? defaultIcon}
        {text ?? defaultText}
      </Button>}
      <Popper
        sx={{
          zIndex: 10000,
        }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={'bottom-start'}
      >
        <Paper
          elevation={4}
          sx={{
            border: '1px solid var(--color-selected)',
            marginTop: '0.2rem',
            paddingTop: '0.5rem',
          }}
        >
          <ClickAwayListener
            onClickAway={() => {
              setAnchorEl(null);
              console.log('click away');
            }}
          >
            <div>
              {google_classroom_integration?.enabled && (
                <ListItem
                  disablePadding
                  sx={{ marginBottom: '-1rem' }}
                  onClick={() => {
                    if (
                      !google_classroom_integration?.enabled ||
                      (community?.corridorType === 'EDUCATIONAL' &&
                        !community?.educationalAccessApproved)
                    ) {
                      return;
                    }
                    track(EVENTS.IMPORT_FROM_CLASSROOM, INTERACTIONS.CLICK);

                    setAnchorEl(null);
                    const modal = showModal(ImportGoogleClassroomModal, {
                      onClose: () => {
                        modal.destroy();
                        onCloseModal?.();
                      },
                      onSubmit,
                      communityId,
                    });

                    onModalOpen?.();
                  }}
                >
                  <ListItemButton
                    disabled={
                      !google_classroom_integration?.enabled ||
                      (community?.corridorType === 'EDUCATIONAL' &&
                        !community?.educationalAccessApproved)
                    }
                  >
                    <ListItemText>
                      <Stack alignItems='center' direction='row' gap='1rem'>
                        <img
                          style={{
                            width: '1.5rem',
                          }}
                          src={GoogleClassroomIcon}
                          alt='Google Classroom'
                        />
                        <Stack>
                          <Typography fontWeight='bold' variant='body1'>
                            <Trans>Import from Google Classroom</Trans>
                          </Typography>
                          <Typography
                            sx={{
                              color: 'var(--color-subtext)',
                            }}
                            variant='body2'
                          >
                            <Trans>Add users from your classroom account</Trans>
                            {community?.corridorType === 'EDUCATIONAL' &&
                              !community?.educationalAccessApproved && (
                                <>
                                  <br />
                                  <Typography color='error' variant='caption'>
                                    <Trans>
                                      This feature is locked until your
                                      educational account is approved
                                    </Trans>
                                  </Typography>
                                </>
                              )}
                          </Typography>
                        </Stack>
                      </Stack>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              )}
              <List>
                <ListItem
                  disablePadding
                  onClick={() => {
                    track(EVENTS.INVITE_BY_EMAIL, INTERACTIONS.CLICK);
                    setAnchorEl(null);
                    const modal = showModal(AddMembersEmailModal, {
                      onClose: () => {
                        modal.destroy();
                        onCloseModal?.();
                      },
                      onSubmit,
                      communityId,
                    });

                    onModalOpen?.();
                  }}
                >
                  <ListItemButton>
                    <ListItemText>
                      <Stack alignItems='center' direction='row' gap='1rem'>
                        <EmailIcon
                          style={{
                            fill: 'var(--color-text)',
                          }}
                        />
                        <Stack>
                          <Typography fontWeight='bold' variant='body1'>
                            <Trans>Invite by Email</Trans>
                          </Typography>
                          <Typography
                            sx={{
                              color: 'var(--color-subtext)',
                            }}
                            variant='body2'
                          >
                            <Trans>Send an email invite to people</Trans>
                          </Typography>
                        </Stack>
                      </Stack>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
                <ListItem
                  disablePadding
                  onClick={() => {
                    if (
                      community?.corridorType === 'EDUCATIONAL' &&
                      !community?.educationalAccessApproved
                    ) {
                      return;
                    }
                    track(EVENTS.CREATE_STUDENT_LOGINS, INTERACTIONS.CLICK);
                    setAnchorEl(null);
                    const modal = showModal(AddMembersLoginModal, {
                      onClose: () => {
                        modal.destroy();
                        onCloseModal?.();
                      },
                      communityId,
                      onSubmit,
                    });

                    onModalOpen?.();
                  }}
                >
                  <ListItemButton
                    disabled={
                      community?.corridorType === 'EDUCATIONAL' &&
                      !community?.educationalAccessApproved
                    }
                  >
                    <ListItemText>
                      <Stack alignItems='center' direction='row' gap='1rem'>
                        <KeyIcon
                          style={{
                            fill: 'var(--color-text)',
                          }}
                        />
                        <Stack>
                          <Typography fontWeight='bold' variant='body1'>
                            <Trans>Create Student Logins (csv)</Trans>
                          </Typography>
                          <Typography
                            sx={{
                              color: 'var(--color-subtext)',
                            }}
                            variant='body2'
                          >
                            <Trans>
                              Create credentials for students to join
                            </Trans>
                            {community?.corridorType === 'EDUCATIONAL' &&
                              !community?.educationalAccessApproved && (
                                <>
                                  <br />
                                  <Typography color='error' variant='caption'>
                                    <Trans>
                                      This feature is locked until your
                                      educational account is approved
                                    </Trans>
                                  </Typography>
                                </>
                              )}
                          </Typography>
                        </Stack>
                      </Stack>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              </List>
            </div>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </>
  );
}

export default function CommunityMembersExplorer(p: {
  listQuery?: string;
  communityId?: string;
}) {
  const { _ } = useLingui();
  const params = useParams<DashboardParams>();
  const history = useHistory();

  const communityId = p.communityId ?? params.communityId;
  const [selectedFilter, setSelectedFilter] = useSearchParam('filter', '$all');
  const [selectedGroupId, setSelectedGroupId] = useSearchParam('groupId');
  const [didCopyLink, setCopyLink] = useState(false);

  const [filterText, setFilterText] = useState('');

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { google_classroom_integration } = useFlags([
    'google_classroom_integration',
  ]);

  useEffect(() => {
    if (didCopyLink) {
      const timer = setTimeout(() => {
        setCopyLink(false);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [didCopyLink]);

  const { community, loadCommunityDetails } = useCommunityDetails({
    communityId,
  });

  useEffect(() => {
    loadCommunityDetails();
  }, []);

  useEvt((ctx) => {
    notificationsEvt.attach(ctx, ({ eventName, content }) => {
      if (eventName === NOTIFICATION_EVENTS.COMMUNITY_NEW_MEMBER) {
        loadCommunityDetails();
      }
    });
  }, []);

  const {
    markMemberApproved,
    markAllMembersApproved,
    removeMember,
    changeRoles,
  } = useCommunityDetails({
    communityId,
  });

  const members = community?.members ?? [];

  const googleClassrooms = community?.googleClassrooms ?? [];

  const canManageUsers = useMemo(
    () => !!community?.roles?.find((it) => it === 'OWNER' || it === 'MANAGER'),
    [community],
  );
  const canAddGroups = useMemo(
    () =>
      !!community?.roles?.find(
        (it) => it === 'OWNER' || it === 'MANAGER' || it === 'HOST',
      ),
    [community],
  );

  const memberGroupings = useMemo(() => {
    const memberGroups: MemberGroups = {
      pendingApproval: [],
      admin: [],
      managers: [],
      adminOrManagers: [],
      emailMembers: [],
      loginMembers: [],
      participants: [],
      reported: [],
      blocked: [],
      pendingInvitedAdminOrManagers: [],
      pendingInvitedHosts: [],
      pendingInvitedParticipants: [],
    };
    for (const m of members) {
      if (m.isBlocked) {
        memberGroups.blocked.push(m);
      } else if (!m.isApproved) {
        memberGroups.pendingApproval.push(m);
      } else if (
        m.roles.indexOf('OWNER') >= 0 ||
        m.pendingRoles.indexOf('OWNER') >= 0
      ) {
        memberGroups.admin.push(m);
        memberGroups.adminOrManagers.unshift(m);
        if (m.invitationCodeId) {
          memberGroups.pendingInvitedAdminOrManagers.unshift(m);
        }
      } else if (
        m.roles.indexOf('MANAGER') >= 0 ||
        m.pendingRoles.indexOf('MANAGER') >= 0
      ) {
        memberGroups.managers.push(m);
        memberGroups.adminOrManagers.push(m);
        if (m.invitationCodeId) {
          memberGroups.pendingInvitedAdminOrManagers.push(m);
        }
      } else if (m.roles.indexOf('HOST') >= 0) {
        // memberGroups.hosts.push(m);
        memberGroups.participants.push(m);
        if (m.invitationCodeId) {
          memberGroups.pendingInvitedHosts.push(m);
        }

        if (m.email) {
          memberGroups.emailMembers.push(m);
        } else {
          memberGroups.loginMembers.push(m);
        }
      } else {
        memberGroups.participants.push(m);
        if (m.invitationCodeId) {
          memberGroups.pendingInvitedParticipants.push(m);
        }

        if (m.email) {
          memberGroups.emailMembers.push(m);
        } else {
          memberGroups.loginMembers.push(m);
        }
      }
      if (m.isReported) {
        memberGroups.reported.push(m);
      }
    }
    return memberGroups;
  }, [members]);

  const memberGroups = useMemo(() => {
    if (filterText) {
      const text = filterText.toLowerCase();
      return Object.fromEntries(
        Object.entries(memberGroupings).map(([key, members]) => {
          return [
            key,
            members.filter((m) => {
              return (
                m.user?.email?.toLowerCase().includes(text) ||
                m?.email?.toLowerCase().includes(text) ||
                (m?.user && qualifiedUserName(m?.user)?.includes(text))
              );
            }),
          ];
        }),
      ) as MemberGroups;
    }
    return memberGroupings;
  }, [filterText, memberGroupings]);

  const filteredClassrooms = useMemo(() => {
    if (filterText) {
      const text = filterText.toLowerCase();
      return googleClassrooms.filter((c) => {
        return (
          c.name?.toLowerCase().includes(text) ||
          c.section?.toLowerCase().includes(text)
        );
      });
    }
    return googleClassrooms;
  }, [filterText, googleClassrooms]);

  useEffect(() => {
    loadNextPages();
  }, []);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const selectedGroup = useMemo(() => {
    if (!selectedGroupId || !community?.groups) return;
    return community.groups.find((g) => g.id === selectedGroupId);
  }, [selectedGroupId, community?.groups]);

  useEffect(() => {
    if (selectedGroupId && !selectedGroup) {
      setSelectedGroupId(null);
      setSelectedFilter('$all');
    }
  }, [selectedGroupId, selectedGroup]);

  const selectedGroupMembers = useMemo(() => {
    if (!selectedGroup) return [];
    return (
      community?.members?.filter((m) => {
        if (selectedGroup?.googleClassroom?.id) {
          return m.googleClassrooms?.find(
            (gc) => gc.id === selectedGroup.googleClassroom.id,
          );
        }
        if (!m.user) return false;
        if (selectedGroup.userIds.indexOf(m.user.id) >= 0) {
          return true;
        }
      }) ?? []
    );
  }, [selectedGroup, members]);

  const filterTags = useMemo(() => {
    if (canManageUsers) return getMemberFilterTags();
    return getMemberFilterTags().filter((it) => !it.managerOnly);
  }, [canManageUsers, _]);

  if (!communityId || !community) return null;

  if (community?.deactivatedAt) {
    return <Redirect to={`/app/dashboard/${communityId}`} />;
  }

  const onSelectUser = selectionMode
    ? (userId: string, selected: boolean) => {
      setSelectedUsers((prev) => {
        console.log('selected', selected, userId);
        if (selected) {
          return uniq([...prev, userId]);
        }
        return prev.filter((it) => it !== userId);
      });
    }
    : undefined;

  return (
    <div className={C.container}>
      <div className={C.primaryHeader}>
        <Grid container alignItems='center' rowGap='1.5rem'>
          <Grid item lg={6} xs={12}>
            <Stack direction='row' alignItems='center'>
              <Typography variant='h2' fontSize='1.2rem'>
                <Trans>Members</Trans>
              </Typography>
              <DotSeparator />
              {
                <Typography mr='2rem' variant='h4' color='var(--color-subtext)'>
                  {community?.members?.length}{' '}
                </Typography>
              }
              {canManageUsers && <AddMembersButton communityId={communityId} />}
            </Stack>
          </Grid>
          <Grid item lg={6} xs={12}>
            <Stack
              direction='row'
              justifyContent={isSmallScreen ? 'flex-start' : 'flex-end'}
            >
              <CommunityJoinLink communityId={communityId} />
            </Stack>
          </Grid>
        </Grid>
      </div>
      <Stack
        mt={3}
        mb={2}
        gap={2}
        rowGap='1.5rem'
        direction={isSmallScreen ? 'column' : 'row'}
        alignItems={isSmallScreen ? 'flex-start' : 'center'}
      >
        <OutlinedInput
          fullWidth
          size='small'
          style={{
            height: '24px',
            flexGrow: 1,
            fontSize: 'var(--font-size-subtext)',
          }}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={_(msg`Find a member`)}
          startAdornment={
            <SearchIcon
              style={{
                marginRight: '0.5rem',
                fill: 'var(--color-subtext)',
                fontSize: '1.2rem',
              }}
            />
          }
          sx={{
            '&.MuiOutlinedInput-root': {
              paddingLeft: '10px',
            },
          }}
        />
        <TagList
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '10px',
          }}
          selectedId={selectedFilter!}
          onSelect={(tagId) => {
            setSelectedFilter(tagId);
            setSelectedGroupId(null);
          }}
          tags={filterTags.filter((it) => {
            if (
              community?.corridorType === 'PERSONAL' &&
              it.id === 'loginMembers'
            ) {
              return false;
            }
            return true;
          })}
        />
      </Stack>
      <FlexRow
        style={{
          marginBottom: '10px',
          paddingBottom: '30px',
          borderBottom: '1px solid var(--color-selected)',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        {canAddGroups ? (
          <>
            <NewGroupControl communityId={communityId} />
          </>
        ) : (
          <></>
        )}
        <>
          {community.groups.map((group) => {
            return (
              <GroupChip
                label={
                  <Stack gap='0.3rem' alignItems='center' direction='row'>
                    {group?.googleClassroom?.id && (
                      <GoogleIcon style={{ width: '1rem' }} />
                    )}
                    {group.name}
                  </Stack>
                }
                key={group.id}
                onSelect={() => {
                  setSelectedGroupId(group.id);
                  setSelectedFilter('$group');
                }}
                onDeselect={() => {
                  setSelectedGroupId(null);
                  setSelectedFilter('$all');
                }}
                isSelected={group.id === selectedGroupId}
              />
            );
          })}
        </>
      </FlexRow>

      {canManageUsers ? (
        <Stack
          sx={{
            height: '24px',
          }}
          mb={2}
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
        >
          {selectionMode && (
            <Box mr={1}>
              <Checkbox
                title={selectedUsers.length > 0 ? 'Deselect all' : 'Select all'}
                size='small'
                onClick={() => {
                  const users: string[] = [];

                  // add email members and login members
                  for (const member of memberGroups.emailMembers) {
                    users.push(member?.user?.id || member.email);
                  }
                  for (const member of memberGroups.loginMembers) {
                    users.push(member?.user?.id);
                  }

                  if (selectedUsers.length === users.length) {
                    setSelectedUsers([]);
                  } else {
                    setSelectedUsers(users);
                  }
                }}
              />
              <Typography
                color={
                  selectedUsers.length > 0
                    ? 'var(--color-primary)'
                    : 'var(--color-subtext)'
                }
                variant='caption'
              >
                <Trans>{selectedUsers.length} members selected.</Trans>
              </Typography>
              <LoadingButton
                sx={{
                  marginLeft: '0.5rem',
                  borderRadius: '20px',
                  fontSize: 'var(--font-size-subtext) !important',
                }}
                loading={isLoading}
                disabled={selectedUsers.length === 0}
                onClick={async () => {
                  if (selectedUsers.length === 0) return;

                  let title;

                  if (selectedUsers.length === 1) {
                    const member = members.find((m) => {
                      return (
                        m?.user?.id === selectedUsers[0] ||
                        m.email === selectedUsers[0]
                      );
                    });
                    title = _(
                      msg`Are you sure you want to remove ${member?.user ? qualifiedUserName(member?.user) : member?.email} from the community?`,
                    );
                  } else {
                    title = _(
                      msg`Are you sure you want to remove ${selectedUsers.length} members from the community?`,
                    );
                  }

                  const { isConfirmed } = await swal.fire({
                    title,
                    showCancelButton: true,
                    text: _(
                      msg`They will not be able to access this community ever again.`,
                    ),
                    confirmButtonText: _(msg`Yes`),
                    cancelButtonText: _(msg`Cancel`),
                  });

                  if (!isConfirmed) return;

                  const usersToDelete = selectedUsers.map((userId) => {
                    if (EMAIL_REGEX.test(userId)) {
                      return {
                        email: userId,
                      };
                    }

                    return {
                      userId,
                    };
                  });

                  setIsLoading(true);
                  try {
                    await httpClient.post(
                      `/community/${communityId}/bulk-delete-users`,
                      {
                        users: usersToDelete,
                      },
                    );
                  } catch (error) {
                    reportServerError({
                      error,
                      title: _(msg`Failed to delete users`),
                    });
                    setIsLoading(false);
                    return;
                  }

                  removeMember(usersToDelete);
                  setIsLoading(false);

                  Swal.fire({
                    title: _(msg`Success`),
                    text: _(msg`Selected members have been removed!`),
                    icon: 'success',
                  });
                }}
                size='small'
                variant='outlined'
                color='error'
                startIcon={
                  <PersonRemoveAlt1Icon
                    sx={{
                      width: 'var(--font-size-subtext)',
                    }}
                  />
                }
              >
                <Trans>Remove</Trans>
              </LoadingButton>
            </Box>
          )}
          <Box
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelectionMode((prev) => !prev);
              setSelectedUsers([]);
            }}
          >
            <Button
              variant='outlined'
              sx={{
                borderRadius: '20px',
                fontSize: 'var(--font-size-subtext) !important',
              }}
              size='small'
              title={
                selectionMode ? 'Cancel selection' : 'Select multiple members'
              }
              startIcon={
                selectionMode ? (
                  <Cancel
                    sx={{
                      width: 'var(--font-size-subtext)',
                    }}
                  />
                ) : (
                  <ModeEdit
                    style={{
                      width: 'var(--font-size-subtext)',
                      fill: 'var(--color-primary)',
                    }}
                  />
                )
              }
            >
              {selectionMode ? t`Cancel` : t`Select`}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box mt={2} />
      )}
      {community?.id && selectedGroup && (
        <>
          <div
            className={C.secondaryHeader}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              textAlign: 'left',
            }}
          >
            {selectedGroup?.googleClassroom?.id && (
              <img
                src={GoogleClassroomIcon}
                style={{ width: '1.5rem', marginRight: '0.4rem' }}
              />
            )}
            <div>{selectedGroup.name}</div>
            {canManageUsers && (
              <GroupMenuTrigger
                communityId={community.id}
                groupId={selectedGroup.id}
                style={{ marginLeft: '1rem' }}
                selectedGroupMembers={selectedGroupMembers}
              />
            )}
          </div>
          <UsersGrid
            selectedUserIds={selectedUsers}
            checkboxOrientation='right'
            onSelect={onSelectUser}
            currentUserRoles={community?.roles ?? []}
            communityId={community?.id}
            query={p.listQuery ?? ''}
            members={selectedGroupMembers}
            onRemoveUser={removeMember}
            onChangeRoles={changeRoles}
            showEmail={canManageUsers}
          />
        </>
      )}
      {((canManageUsers &&
        selectedFilter === '$all' &&
        memberGroups.pendingApproval.length > 0) ||
        selectedFilter === 'pendingApproval') && (
          <>
            <div
              className={C.secondaryHeader}
              style={{
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <PendingActionsIcon />
              <Box ml={1} />
              <span>
                <Trans>Join Requests</Trans>
              </span>{' '}
              {canManageUsers && !isEmpty(memberGroups.pendingApproval) && (
                <Button
                  variant='text'
                  onClick={async () => {
                    try {
                      await httpClient.post(
                        `/community/${community.id}/members/approve`,
                        {
                          memberIds: compact(
                            memberGroups.pendingApproval.map((it) => it.user?.id),
                          ),
                        },
                      );
                      markAllMembersApproved();
                    } catch (error) {
                      reportServerError({
                        title: _(msg`Failed to approve user`),
                        error,
                      });
                    }
                  }}
                >
                  <Trans>Add all</Trans>
                </Button>
              )}
            </div>
            <UsersGrid
              selectedUserIds={selectedUsers}
              checkboxOrientation='right'
              onSelect={onSelectUser}
              currentUserRoles={community?.roles ?? []}
              communityId={community?.id}
              query={p.listQuery ?? ''}
              members={memberGroups.pendingApproval}
              onRemoveUser={removeMember}
              onChangeRoles={changeRoles}
              onApproved={markMemberApproved}
              showEmail={true}
            />
          </>
        )}
      {((selectedFilter === '$all' &&
        memberGroups.adminOrManagers.length > 0) ||
        selectedFilter === 'adminsAndManagers' ||
        (canManageUsers &&
          selectedFilter === 'invited' &&
          memberGroups.pendingInvitedAdminOrManagers.length > 0)) && (
          <>
            <div
              className={C.secondaryHeader}
              style={{
                textAlign: 'left',
              }}
            >
              <AdminPanelSettingsIcon />
              <Box ml={1} />
              <Trans>Admins / Managers</Trans>
              <DotSeparator />
              {
                <Typography variant='body1' color='var(--color-subtext)'>
                  {selectedFilter === 'invited'
                    ? memberGroups.pendingInvitedAdminOrManagers.length
                    : memberGroups.adminOrManagers.length}
                </Typography>
              }
            </div>
            <UsersGrid
              selectedUserIds={selectedUsers}
              checkboxOrientation='right'
              onSelect={undefined}
              currentUserRoles={community?.roles ?? []}
              communityId={community?.id}
              query={p.listQuery ?? ''}
              members={
                selectedFilter === 'invited'
                  ? memberGroups.pendingInvitedAdminOrManagers
                  : memberGroups.adminOrManagers
              }
              onRemoveUser={removeMember}
              onChangeRoles={changeRoles}
              showEmail={true}
            />
          </>
        )}
      {selectedFilter === 'admins' && (
        <>
          <div className={C.secondaryHeader} style={{ textAlign: 'left' }}>
            <Trans>Admins</Trans>
          </div>
          <UsersGrid
            selectedUserIds={selectedUsers}
            checkboxOrientation='right'
            onSelect={onSelectUser}
            currentUserRoles={community?.roles ?? []}
            communityId={community?.id}
            query={p.listQuery ?? ''}
            members={memberGroups.admin}
            onRemoveUser={removeMember}
            onChangeRoles={changeRoles}
            showEmail={true}
          />
        </>
      )}
      {selectedFilter === 'managers' && (
        <>
          <div className={C.secondaryHeader} style={{ textAlign: 'left' }}>
            <Trans>Managers</Trans>
          </div>
          <UsersGrid
            selectedUserIds={selectedUsers}
            checkboxOrientation='right'
            onSelect={onSelectUser}
            currentUserRoles={community?.roles ?? []}
            communityId={community?.id}
            query={p.listQuery ?? ''}
            members={memberGroups.managers}
            onRemoveUser={removeMember}
            onChangeRoles={changeRoles}
            showEmail={true}
          />
        </>
      )}
      {/* ((selectedFilter === '$all' && memberGroups.hosts.length > 0) ||
        (canManageUsers &&
          selectedFilter === 'invited' &&
          memberGroups.pendingInvitedHosts.length > 0) ||
        selectedFilter === 'hosts') && (
        <>
          <div className={C.secondaryHeader} style={{ textAlign: 'left' }}>
            Hosts
          </div>
          <UsersGrid
          selectedUserIds={selectedUsers}
            checkboxOrientation='right'
            onSelect={onSelectUser
            }}
            currentUserRoles={community?.roles ?? []}
            communityId={community?.id}
            query={p.listQuery ?? ''}
            members={
              selectedFilter === 'invited'
                ? memberGroups.pendingInvitedHosts
                : memberGroups.hosts
            }
            onRemoveUser={removeMember}
            onChangeRoles={changeRoles}
            showEmail={true}
          />
        </>
          ) */}

      {/* {(selectedFilter === '$all' || selectedFilter === 'googleClassroom') &&
        google_classroom_integration?.enabled && (
          <>
            <div
              className={C.secondaryHeader}
              style={{ textAlign: 'left', marginTop: '2rem' }}
            >
              <GoogleIcon
                sx={{
                  fill: 'var(--color-text)',
                  width: '1.5rem',
                }}
              />
              <Box ml={1} />
              <Trans>Google Classrooms</Trans>
              <DotSeparator />
              {
                <Typography variant='body1' color='var(--color-subtext)'>
                  {filteredClassrooms.length}
                </Typography>
              }
            </div>
            {filteredClassrooms?.length > 0 && (
              <GoogleClassroomGrid
                classrooms={filteredClassrooms}
                communityId={communityId}
              />
            )}
          </>
        )} */}
      {(selectedFilter === '$all' ||
        selectedFilter === 'emailMembers' ||
        (canManageUsers &&
          selectedFilter === 'invited' &&
          memberGroups.pendingInvitedParticipants.length > 0) ||
        selectedFilter === 'participants') && (
          <>
            <div
              className={C.secondaryHeader}
              style={{ textAlign: 'left', marginTop: '2rem' }}
            >
              <EmailIcon />
              <Box ml={1} />
              <Trans>Members</Trans>
              <Typography
                variant='caption'
                style={{
                  color: 'var(--color-text)',
                }}
              >
                &nbsp;&nbsp;<Trans>(with email)</Trans>
              </Typography>
              <DotSeparator />
              {
                <Typography variant='body1' color='var(--color-subtext)'>
                  {selectedFilter === 'invited'
                    ? memberGroups.pendingInvitedParticipants.length
                    : memberGroups.emailMembers.length}
                </Typography>
              }
            </div>
            <UsersGrid
              selectedUserIds={selectedUsers}
              checkboxOrientation='right'
              onSelect={onSelectUser}
              currentUserRoles={community?.roles ?? []}
              communityId={community?.id}
              query={p.listQuery ?? ''}
              members={
                selectedFilter === 'invited'
                  ? memberGroups.pendingInvitedParticipants
                  : memberGroups.emailMembers
              }
              onRemoveUser={removeMember}
              onChangeRoles={changeRoles}
              showEmail={canManageUsers}
              singleLine={!canManageUsers}
            />
          </>
        )}

      {community?.corridorType === 'EDUCATIONAL' &&
        (selectedFilter === '$all' || selectedFilter === 'loginMembers') && (
          <>
            <div
              className={C.secondaryHeader}
              style={{ textAlign: 'left', marginTop: '2rem' }}
            >
              <KeyIcon />
              <Box ml={1} />
              <Trans>Members</Trans>
              <Typography
                variant='caption'
                style={{
                  color: 'var(--color-text)',
                }}
              >
                &nbsp;&nbsp;<Trans>(with student logins)</Trans>
              </Typography>
              <DotSeparator />
              {
                <Typography variant='body1' color='var(--color-subtext)'>
                  {memberGroups.loginMembers.length}
                </Typography>
              }
              {memberGroups.loginMembers.length > 0 && (
                <Box
                  sx={{
                    marginLeft: 'auto',
                  }}
                >
                  {canManageUsers && (
                    <DownloadCSVButton communityId={communityId} />
                  )}
                </Box>
              )}
            </div>
            <UsersGrid
              selectedUserIds={selectedUsers}
              checkboxOrientation='right'
              onSelect={onSelectUser}
              currentUserRoles={community?.roles ?? []}
              communityId={community?.id}
              query={p.listQuery ?? ''}
              members={memberGroups.loginMembers.sort((a, b) =>
                a.user?.username?.localeCompare(b.user?.username),
              )}
              onRemoveUser={removeMember}
              onChangeRoles={changeRoles}
              showEmail={canManageUsers}
              singleLine={!canManageUsers}
            />
          </>
        )}
      {canManageUsers && selectedFilter === 'blocked' && (
        <>
          <div className={C.secondaryHeader} style={{ textAlign: 'left' }}>
            <BlockIcon />
            <Box ml={1} />
            <Trans>Blocked Users</Trans>
          </div>
          <UsersGrid
            selectedUserIds={selectedUsers}
            checkboxOrientation='right'
            onSelect={onSelectUser}
            currentUserRoles={community?.roles ?? []}
            communityId={community?.id}
            query={p.listQuery ?? ''}
            members={memberGroups.blocked}
            onRemoveUser={removeMember}
            onChangeRoles={changeRoles}
            showEmail={true}
            showControls={false}
          />
        </>
      )}
      {canManageUsers && selectedFilter === 'reported' && (
        <>
          <div className={C.secondaryHeader} style={{ textAlign: 'left' }}>
            <ReportIcon />
            <Box ml={1} />
            <Trans>Reported Users</Trans>
          </div>
          <UsersGrid
            selectedUserIds={selectedUsers}
            checkboxOrientation='right'
            onSelect={onSelectUser}
            currentUserRoles={community?.roles ?? []}
            communityId={community?.id}
            query={p.listQuery ?? ''}
            members={memberGroups.reported}
            onRemoveUser={removeMember}
            onChangeRoles={changeRoles}
            showEmail={true}
          />
        </>
      )}
    </div>
  );

  function loadNextPages() {
    import('components/community-member-adder/CommunityMemberAdder');
  }
}

export function GoogleClassroomGrid(p: {
  classrooms: {
    id: string;
    name?: string | null;
    section?: string | null;
  }[];
  communityId: string;
}) {
  const { _ } = useLingui();

  const { loadCommunityDetails } = useCommunityDetails({
    communityId: p.communityId,
    autoLoad: false,
  });

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'google-classroom-menu',
  });
  return (
    <>
      <ul className={C.userGrid}>
        {p.classrooms?.map((classroom) => (
          <ListItem key={classroom.id}>
            <ListItemText>
              <Stack alignItems='center' direction='row' gap='1rem'>
                <img
                  style={{
                    width: '1.5rem',
                  }}
                  src={GoogleClassroomIcon}
                  alt='Google Classroom'
                />
                <Stack>
                  <Typography fontWeight='bold' variant='body1'>
                    {classroom.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--color-subtext)',
                    }}
                    variant='body2'
                  >
                    {classroom.section}
                  </Typography>
                </Stack>
              </Stack>
            </ListItemText>
            <ListItemSecondaryAction>
              <Button
                {...bindTrigger(popupState)}
                style={{
                  padding: '5px',
                  minWidth: 'auto',
                }}
                className={cc([C.auxBtn])}
              >
                <MoreHorizIcon
                  style={{
                    color: 'var(--color-subtext)',
                    fontSize: '1.1rem',
                  }}
                />
              </Button>
              <Menu {...bindMenu(popupState)}>
                <MenuItem
                  onClick={async () => {
                    popupState.close();
                    await httpClient.delete(
                      `/community/${p.communityId}/classroom-invitations`,
                      {
                        data: {
                          classroomId: classroom.id,
                        },
                      },
                    );

                    await loadCommunityDetails();
                  }}
                >
                  <ListItemIcon>
                    <CancelRounded />
                  </ListItemIcon>
                  <ListItemText>
                    <Trans>Disconnect Classroom</Trans>
                  </ListItemText>
                </MenuItem>
              </Menu>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </ul>
    </>
  );
}

type Member = NonNullable<CommunityDetails['members']>[number];

export function UsersGrid(p: {
  query: string;
  communityId: string;
  code?: string;
  members: Member[];
  onRemoveUser: (users: { userId?: string; email?: string }[]) => void;
  onChangeRoles: (userId: string, roles: string[]) => void;
  onApproved?: (userId: string) => void;
  currentUserRoles: string[];
  showEmail: boolean;
  singleLine?: boolean;
  showControls?: boolean;
  onSelect?: (userId: string, selected: boolean) => void;
  checkboxOrientation?: 'left' | 'right';
  selectedUserIds?: string[];
}) {
  const { _ } = useLingui();
  const profileState = useStore(userProfileAtom);
  const [editingUser, setEditingUser] = useState<CommunityUser | null>(null);

  if (isEmpty(p.members)) {
    // return <EmptyMessage message={_(msg`No users.`)} />;
    return <></>;
  }

  return (
    <>
      <ul className={C.userGrid}>
        {p.members?.map((member) => {
          const isCurrent = member.user?.id
            ? profileState.userProfile?.id === member.user?.id
            : false;

          return (
            <UserListItem
              isCurrent={isCurrent}
              query={p.query}
              key={member.user?.id || member.email || member.invitationCodeId}
              member={member}
              communityId={p.communityId}
              onRemoveUser={p.onRemoveUser}
              onChangeRoles={p.onChangeRoles}
              onApproved={p.onApproved}
              showEmail={p.showEmail}
              currentUserRoles={p.currentUserRoles}
              isMemberEditable={(m) => {
                return !!m.user?.loginAllowanceId;
              }}
              singleLine={p.singleLine}
              showControls={p.showControls}
              onEditMember={(m) => {
                if (m.user) {
                  setEditingUser(m.user);
                }
              }}
              checkboxOrientation={p.checkboxOrientation}
              onSelect={isCurrent ? undefined : p.onSelect} // Prevent selecting self
              isSelected={
                p?.selectedUserIds?.includes(member.user?.id ?? '') ||
                p?.selectedUserIds?.includes(member.email ?? '')
              }
            />
          );
        })}
      </ul>
      {editingUser && (
        <UsernameEditModal
          communityId={p.communityId}
          user={editingUser}
          onClose={() => {
            setEditingUser(null);
          }}
        />
      )}
    </>
  );
}

export function UserList(p: {
  query: string;
  communityId: string;
  members: Member[];
  onRemoveUser?: (users: { userId?: string; email?: string }[]) => void;
  onChangeRoles?: (userId: string, roles: string[]) => void;
  onApproved?: (userId: string) => void;
  selectedUserIds?: string[];
  mandatoryUserIds?: string[];
  currentUserRoles?: string[];
  showControls?: boolean;
  checkboxOrientation?: 'left' | 'right';
  onSelect?: (userId: string, didSelect: boolean) => void;
  showEmail?: boolean;
  singleLine?: boolean;
  subText?: boolean;
}) {
  const profileState = useStore(userProfileAtom);
  return (
    <>
      <List>
        {p.members?.map((member) => (
          <UserListItem
            query={p.query}
            key={member.user?.id}
            member={member}
            communityId={p.communityId}
            onRemoveUser={p.onRemoveUser}
            onChangeRoles={p.onChangeRoles}
            onApproved={p.onApproved}
            showControls={p.showControls}
            showEmail={p.showEmail}
            subText={p.subText}
            isCurrent={
              member.user?.id
                ? member.user?.id === profileState.userProfile?.id
                : false
            }
            currentUserRoles={p.currentUserRoles ?? []}
            isSelected={
              !!member.user &&
              (p.selectedUserIds?.indexOf(member.user.id) ?? -1) >= 0
            }
            onSelect={p.onSelect}
            checkboxOrientation={p.checkboxOrientation}
            disabled={
              (p.mandatoryUserIds || []).indexOf(member.user?.id ?? '') >= 0
            }
            singleLine={p.singleLine}
          />
        ))}
      </List>
    </>
  );
}

export function UserListItem(p: {
  communityId: string;
  query: string;
  member: Member;
  isCurrent?: boolean;
  currentUserRoles: string[];
  onRemoveUser?: (users: { userId?: string; email?: string }[]) => void;
  onChangeRoles?: (userId: string, roles: string[]) => void;
  onApproved?: (userId: string) => void;
  isSelected?: boolean;
  showEmail?: boolean;
  showControls?: boolean;
  checkboxOrientation?: 'left' | 'right';
  isMemberEditable?: (member: Member) => boolean;
  onEditMember?: (member: Member) => void;
  onSelect?: (userId: string, didSelect: boolean) => void;
  disabled?: boolean;
  singleLine?: boolean;
  small?: boolean;
  subText?: boolean;
}) {
  const { _ } = useLingui();
  const { user, roles, pendingRoles, email } = p.member;

  const uniqRoles = useMemo((): string[] => {
    return uniq((roles ?? []).concat(pendingRoles ?? []));
  }, [roles, pendingRoles]);
  const isManager = useMemo(
    () =>
      !!p.currentUserRoles?.find((it) => it === 'OWNER' || it === 'MANAGER'),
    [p.currentUserRoles],
  );

  const showEmail = p.showEmail !== false;

  const displayName = useMemo(() => {
    const nameParts = [user?.firstName, user?.lastName].filter(
      Boolean,
    ) as string[];
    let displayName = nameParts.map(capitalize).join(' ').trim();
    if (!displayName && showEmail && email) displayName = email;
    if (p.isCurrent && showEmail && email)
      displayName += `\u00A0${_(msg`(You)`)}`;
    return displayName;
  }, [user]);

  const shouldShow = useMemo(() => {
    // if (!email && !user?.loginAllowanceId) return false;
    const q = p.query.trim().toLowerCase();
    if (!q) return true;
    const firstName = user?.firstName?.toLowerCase();
    const lastName = user?.lastName?.toLowerCase();
    const nEmail = email?.toLowerCase();
    const username = user?.username?.toLowerCase();
    if (
      (firstName && firstName.indexOf(q) === 0) ||
      (lastName && lastName.indexOf(q) === 0) ||
      (nEmail && nEmail.indexOf(q) === 0) ||
      (username && username.indexOf(q) === 0)
    )
      return true;
    return false;
  }, [user?.firstName, user?.lastName, user?.username, email, p.query]);

  if (!shouldShow) {
    return null;
  }

  const showActions =
    p.showControls !== false &&
    isManager &&
    !p.member.isSelf &&
    p.member.isApproved;
  return (
    <ListItem
      data-user-id={user?.id}
      style={{ fontSize: 'var(--font-size-primary)' }}
    >
      {p.onSelect && p.checkboxOrientation !== 'right' && (
        <ListItemIcon>
          <Checkbox
            checked={p.isSelected}
            onChange={(_event, checked) => {
              if (!p.member.user?.id) return;
              p.onSelect?.(p.member.user.id, checked);
            }}
            disabled={p.disabled}
          />
        </ListItemIcon>
      )}
      <ListItemAvatar>
        <UserAvatar
          user={user}
          size={!p.small ? 35 : 45}
          className={C.avatar}
        />
      </ListItemAvatar>
      <ListItemText>
        <div
          style={{
            display: p.singleLine ? 'inline-block' : 'block',
            color: p.subText ? 'var(--color-subtext)' : 'var(--color-text)',
          }}
        >
          {displayName || user?.username}
        </div>
        <InlineList
          style={{
            fontSize: 'var(--font-size-subtext)',
            display: p.singleLine ? 'inline-block' : 'block',
            marginLeft: p.singleLine ? '5px' : '0',
          }}
        >
          {!showEmail && (
            <li>
              <span
                style={{
                  color: 'var(--color-subtext)',
                  fontSize: 'var(--font-size-primary)',
                }}
              >
                {p.isCurrent ? _(msg`(You)`) : ''}
              </span>
            </li>
          )}
          <RoleList roles={uniqRoles} />
          {showEmail && email && displayName !== email && (
            <li style={{ color: 'var(--color-subtext)' }}>{email}</li>
          )}
          {p.member.invitationCodeId && (
            <li
              style={{ color: 'var(--color-subtext)', verticalAlign: 'middle' }}
            >
              <Trans>Invited</Trans>
            </li>
          )}
          {(p.member.googleClassrooms?.length || 0) > 0 && (
            <Tooltip title={_(msg`Member was added via Google Classroom`)}>
              <GoogleIcon
                sx={{
                  color: 'var(--color-subtext)',
                  fontSize: '1rem',
                  marginLeft: '5px',
                  verticalAlign: 'middle',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
        </InlineList>
      </ListItemText>
      {isManager && !p.member.isApproved && user && (
        <>
          <ListItemSecondaryAction>
            <FlexRow>
              {p.member.requestMessage && (
                <PopupState variant='popover' popupId='request-message-popover'>
                  {(popupState) => (
                    <>
                      <Button variant='text' {...bindTrigger(popupState)}>
                        <Badge
                          sx={{
                            '& .MuiBadge-dot': {
                              transform: 'translateX(-2px)',
                            },
                          }}
                          color='warning'
                          variant='dot'
                        >
                          <CommentIcon
                            style={{
                              color: 'var(--color-subtext)',
                              marginRight: '5px',
                            }}
                          />
                        </Badge>
                      </Button>
                      <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'center',
                        }}
                      >
                        <div style={{ padding: '10px' }}>
                          {p.member.requestMessage}
                        </div>
                      </Popover>
                    </>
                  )}
                </PopupState>
              )}
              <Tooltip title={_(msg`Approve`)}>
                <Button
                  size='small'
                  variant='contained'
                  onClick={async () => {
                    try {
                      await httpClient.post(
                        `/community/${p.communityId}/members/${user.id}/approve`,
                      );
                      p.onApproved?.(user.id);
                    } catch (error) {
                      reportServerError({
                        title: _(msg`Failed to approve user`),
                        error,
                      });
                    }
                  }}
                  style={{
                    padding: '0 6px',
                    minWidth: 'auto',
                    background: 'var(--color-selected)',
                    color: 'var(--color-subtext)',
                    border: 'none',
                    boxShadow: 'none',
                  }}
                >
                  <Trans>Add</Trans>
                </Button>
              </Tooltip>
              <Tooltip title={_(msg`Reject request`)}>
                <Button
                  size='small'
                  variant='text'
                  style={{
                    padding: '4px',
                    minWidth: 'auto',
                    marginLeft: '5px',
                  }}
                  onClick={async () => {
                    const didConfirm = await confirmRemember({
                      decisionKey: 'rejectJoinRequest',
                      title: _(msg`Reject request to join?`),
                      lingui: _,
                    });
                    if (!didConfirm) return;
                    try {
                      await httpClient.delete(
                        `/community/${p.communityId}/membership?${qs.stringify({
                          userId: user.id,
                        })}`,
                      );
                      p.onRemoveUser?.([{ userId: user.id }]);
                    } catch (error) {
                      reportServerError({
                        title: _(msg`Failed to remove member`),
                        error,
                      });
                    }
                  }}
                >
                  <CloseIcon />
                </Button>
              </Tooltip>
            </FlexRow>
          </ListItemSecondaryAction>
        </>
      )}
      {showActions && (
        <ListItemSecondaryAction>
          {p.onSelect && (
            <Checkbox
              checked={p.isSelected}
              onChange={(_event, checked) => {
                p.onSelect?.(
                  p.member?.user?.id || p?.member?.email || '',
                  checked,
                );
              }}
              disabled={p.disabled}
            />
          )}
          <MemberActionsMenu
            currentUserRoles={p.currentUserRoles}
            communityId={p.communityId}
            member={p.member}
            isMemberEditable={p.isMemberEditable}
            onEditMember={p.onEditMember}
          />
        </ListItemSecondaryAction>
      )}

      {p.onSelect &&
        !showActions &&
        !p.showControls &&
        p.checkboxOrientation === 'right' && (
          <ListItemIcon>
            <Checkbox
              checked={p.isSelected}
              onChange={(_event, checked) => {
                console.log(p.member, checked);
                if (!p.member.user?.id) return;
                p.onSelect?.(p.member.user.id, checked);
              }}
              disabled={p.disabled}
            />
          </ListItemIcon>
        )}
    </ListItem>
  );
}

export function MemberActionsMenu(p: {
  style?: React.CSSProperties;
  className?: string;
  communityId: string;
  member: Member;
  currentUserRoles: string[];
  isMemberEditable?: (member: Member) => boolean;
  onEditMember?: (member: Member) => void;
}) {
  const { _ } = useLingui();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'memberActionsPopup',
  });

  const { isSelf, user, roles, email, invitationCodeId } = p.member;

  const { removeMember, changeRoles, changeInvitationRole } =
    useCommunityDetails({
      communityId: p.communityId,
      autoLoad: false,
    });

  const isOwner = useMemo(
    () => p.currentUserRoles.includes('OWNER'),
    [p.currentUserRoles],
  );
  const switchableRoles = useMemo(() => {
    let eligibleRoles = getSameOrLowerRoles(p.currentUserRoles);
    if (!email) {
      eligibleRoles = eligibleRoles.filter((it) => !it.requiresEmail);
    }
    return new Set(
      eligibleRoles.map((it) => {
        return it.value;
      }),
    );
  }, [p.currentUserRoles, email]);

  const hasSwitchableRole = switchableRoles.size > 1;

  const membershipRoles = useMemo(() => {
    return COMMUNITY_MEMBERSHIP_ROLES.map((mr) => {
      return { ...mr, isAssigned: roles.indexOf(mr.value) >= 0 };
    });
  }, [p.currentUserRoles]);

  const currentMembershipRole = membershipRoles.find(
    (it) => it.isAssigned,
  )?.value;

  const menuItems: ReactChild[] = [];

  if (
    !isSelf &&
    hasSwitchableRole &&
    (!currentMembershipRole || switchableRoles.has(currentMembershipRole))
  ) {
    menuItems.push(
      <MenuItem key='change-role' disabled>
        <ListItemText>
          <Trans>Change Role</Trans>
        </ListItemText>
      </MenuItem>,
    );
    for (const mr of membershipRoles) {
      const isSwitchable = switchableRoles.has(mr.value);
      if (!mr.isAssigned && !isSwitchable) {
        continue;
      }
      const disabled = mr.isAssigned || !isSwitchable;
      menuItems.push(
        <MenuItem
          key={`mr-${mr.value}`}
          disabled={disabled}
          onClick={disabled ? undefined : () => switchToRole(mr)}
        >
          <ListItemIcon>
            <GroupAddIcon />
          </ListItemIcon>
          <ListItemText>{_(mr.label)}</ListItemText>
          {mr.isAssigned && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
        </MenuItem>,
      );
    }
    menuItems.push(<Divider key='divider' />);
  }
  if (p.isMemberEditable?.(p.member)) {
    menuItems.push(
      <MenuItem
        onClick={() => {
          popupState.close();
          p.onEditMember?.(p.member);
        }}
        key='edit-member-menu-item'
      >
        <ListItemIcon>
          <EditIcon style={{ height: '24px', width: '24px' }} />
        </ListItemIcon>
        <ListItemText>
          <Trans>Edit Details</Trans>
        </ListItemText>
      </MenuItem>,
    );
  }

  if (!isSelf && (roles.indexOf('OWNER') < 0 || isOwner)) {
    menuItems.push(
      <MenuItem onClick={removeCommunityMember} key='block-member-menu-item'>
        <ListItemIcon>
          <RemoveCircleOutlineIcon />
        </ListItemIcon>
        <ListItemText>
          <Trans>Block</Trans>
        </ListItemText>
      </MenuItem>,
      <MenuItem onClick={deactivateMember} key='deactivate-member-menu-item'>
        <ListItemIcon>
          <PersonRemoveAlt1Icon />
        </ListItemIcon>
        <ListItemText>
          {user ? (
            <Trans>Remove Member</Trans>
          ) : (
            <Trans>Remove Invitation</Trans>
          )}
        </ListItemText>
      </MenuItem>,
    );
  }

  if (menuItems.length === 0) return null;

  return (
    <>
      <Button
        {...bindTrigger(popupState)}
        style={{
          padding: '5px',
          minWidth: 'auto',
          ...p.style,
        }}
        className={cc([p.className, C.auxBtn])}
      >
        <MoreHorizIcon
          style={{
            color: 'var(--color-subtext)',
            fontSize: '1.1rem',
          }}
        />
      </Button>
      <Menu {...bindMenu(popupState)}>{menuItems}</Menu>
    </>
  );

  async function deactivateMember() {
    popupState.close();
    const didConfirm = await confirmRemember({
      title: _(msg`Are you sure?`),
      decisionKey: 'deactivateMember',
      lingui: _,
    });
    if (!didConfirm) return;
    if (user) {
      await httpClient.delete(
        `/community/${p.communityId}/membership?${qs.stringify({
          userId: user.id,
        })}`,
      );
      removeMember([{ userId: user?.id, email: email ?? undefined }]);
    } else if (email) {
      await httpClient.delete(`/community/${p.communityId}/email`, {
        data: {
          targetEmail: email,
        },
      });
      removeMember([{ email }]);
    }
  }

  async function switchToRole(role: MembershipRole) {
    popupState.close();
    if (user) {
      const rolesToGrant = [role.value];
      const rolesToRevoke = membershipRoles
        .filter((it) => it.isAssigned)
        .map((it) => it.value);
      const didConfirm = await confirmRemember({
        title: _(msg`Are you sure?`),
        decisionKey: 'membershipChange',
        lingui: _,
      });
      if (!didConfirm) return;
      try {
        await httpClient.post(`/community/${p.communityId}/role`, {
          rolesToGrant,
          rolesToRevoke,
          targetUserId: user.id,
        });
        changeRoles(user.id, rolesToGrant);
      } catch (error) {
        reportServerError({
          title: _(msg`Failed to switch role`),
          error,
        });
      }
    } else if (invitationCodeId) {
      try {
        await httpClient.post(`/code/${invitationCodeId}/role`, {
          role: role.value,
        });
        changeInvitationRole(invitationCodeId, [role.value]);
      } catch (error) {
        reportServerError({
          title: _(msg`Failed to switch role`),
          error,
        });
      }
    }
  }

  async function removeCommunityMember() {
    popupState.close();
    const didConfirm = await confirmRemember({
      title: _(msg`Are you sure that you want to block this user?`),
      text: _(msg`They will not be able to access this community ever again.`),
      decisionKey: 'confirmRemove',
      lingui: _,
    });
    if (!didConfirm) return;
    httpClient.post(`/community/${p.communityId}/blocked-users`, {
      username: user?.username,
      email: email,
    });
    removeMember([{ userId: user?.id, email: email ?? undefined }]);
  }
}
